/**
 * The refusals of the HQ Site API (contract §4) as sentences an operator can act on.
 *
 * Every 4xx the module raises is `detail: {code, message, ...extra}`; `message` is the
 * server's English and is never shown. Each code maps to a `superadmin` key, and the extras
 * the sentence needs (the offending placeholders, the limit, the href) are interpolated.
 * Anything else — FastAPI's own 422 array, a network failure — goes through
 * `extractApiError`, the product's one normaliser for those shapes.
 */
import { apiErrorCode, extractApiError } from "../../lib/apiErrors";

const braces = (names) => names.map((n) => `{${n}}`).join(", ");

/** A byte count as whole megabytes ("10 MB"); the only size this module states is its cap. */
export const formatMegabytes = (bytes) => `${Math.round(Number(bytes) / (1024 * 1024))} MB`;

const asList = (v) => (Array.isArray(v) ? v.filter((x) => typeof x === "string") : []);

const MESSAGES = {
  UNKNOWN_CONTENT_KEY: (t, d) => t("hsite.err.unknownKey", { key: d.key ?? "" }),
  WRONG_CONTENT_KIND: (t, d) => t("hsite.err.wrongKind", { key: d.key ?? "" }),
  UNDECLARED_PLACEHOLDER: (t, d) => {
    const allowed = asList(d.allowed);
    return t("hsite.err.undeclaredPlaceholder", {
      locale: String(d.locale ?? "").toUpperCase(),
      placeholders: braces(asList(d.placeholders)),
      allowed: allowed.length > 0 ? braces(allowed) : t("hsite.err.noPlaceholderAllowed"),
    });
  },
  CONTENT_TOO_LONG: (t, d) =>
    t("hsite.err.tooLong", { locale: String(d.locale ?? "").toUpperCase(), max: d.max ?? "" }),
  UNKNOWN_MEDIA: (t) => t("hsite.err.unknownMedia"),
  INVALID_HREF: (t, d) => t("hsite.err.invalidHref", { href: d.href ?? "" }),
  INVALID_LINK_GROUP: (t) => t("hsite.err.invalidLinkGroup"),
  LINK_NOT_FOUND: (t) => t("hsite.err.linkNotFound"),
  MEDIA_NOT_FOUND: (t) => t("hsite.err.mediaNotFound"),
  MEDIA_IN_USE: (t, d) => t("hsite.err.mediaInUse", { count: asReferences(d).length }),
  UNSUPPORTED_MEDIA_TYPE: (t) => t("hsite.err.unsupportedMediaType"),
  MEDIA_TOO_LARGE: (t, d) =>
    (d.max_bytes ? t("hsite.err.mediaTooLarge", { max: formatMegabytes(d.max_bytes) }) : t("hsite.err.mediaTooLargeNoMax")),
  REORDER_MISMATCH: (t) => t("hsite.err.reorderMismatch"),
};

function structuredDetail(e) {
  const detail = e?.response?.data?.detail;
  return detail !== null && typeof detail === "object" && !Array.isArray(detail) ? detail : {};
}

function asReferences(detail) {
  const refs = Array.isArray(detail.references) ? detail.references : [];
  return refs.filter((r) => r && typeof r.type === "string" && typeof r.id === "string");
}

/**
 * @param {unknown} e an axios error from a `siteApi` call
 * @param {(key: string, vars?: object, fallback?: string) => string} t `useT("superadmin")`
 * @returns {string}
 */
export function siteErrorMessage(e, t) {
  // A role without the permission (data.media.read, settings.write) is told so in its language;
  // the server's refusal text is English and names an internal permission key.
  if (e?.response?.status === 403) return t("access.forbiddenAction");
  const format = MESSAGES[apiErrorCode(e)];
  return format ? format(t, structuredDetail(e)) : extractApiError(e, t("hsite.err.generic"), t);
}

/**
 * The references a 409 MEDIA_IN_USE lists — `[{type, id, label}]` — or `[]` for any other
 * error. The media screen shows them so the operator knows what to change first.
 */
export function mediaInUseReferences(e) {
  return apiErrorCode(e) === "MEDIA_IN_USE" ? asReferences(structuredDetail(e)) : [];
}
